import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar, Save, Loader2, X } from 'lucide-react';
import { apiFetch } from '@/lib/apiFetch';
import type { Organization } from '@/types/database';

const TIMEZONE_OPTIONS = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
];

interface OrgOverviewTabProps {
  organization: Organization;
  onRefresh: () => void;
}

export const OrgOverviewTab: React.FC<OrgOverviewTabProps> = ({
  organization,
  onRefresh,
}) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    name: organization.name,
    slug: organization.slug,
    logo: organization.logo ?? '',
    primary_color: organization.primary_color ?? '',
    timezone: organization.timezone,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleEdit = () => {
    if (editing) {
      setForm({
        name: organization.name,
        slug: organization.slug,
        logo: organization.logo ?? '',
        primary_color: organization.primary_color ?? '',
        timezone: organization.timezone,
      });
      setError(null);
    }
    setEditing(!editing);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError('Organization name is required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/organizations/${organization.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          ...form,
          logo: form.logo || null,
          primary_color: form.primary_color || null,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { error?: string }).error ?? 'Save failed');
      }
      setEditing(false);
      onRefresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  return (
    <div className="space-y-6">
      {/* Header card */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div
                className="w-16 h-16 rounded-lg flex items-center justify-center"
                style={{
                  backgroundColor: organization.primary_color
                    ? `${organization.primary_color}20`
                    : '#dbeafe',
                }}
              >
                <span
                  className="text-2xl font-bold"
                  style={{
                    color: organization.primary_color ?? '#2563eb',
                  }}
                >
                  {organization.name.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-xl font-semibold">{organization.name}</h3>
                  <Badge variant="outline" className="font-mono text-xs">
                    {organization.slug}
                  </Badge>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Created {formatDate(organization.created_at)}
                </p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={toggleEdit}>
              {editing ? 'Cancel' : 'Edit'}
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* Edit form or details */}
      <Card>
        <CardHeader>
          <CardTitle>Organization Information</CardTitle>
        </CardHeader>
        <CardContent>
          {editing ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Name *</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </div>
                <div className="space-y-1">
                  <Label>Slug</Label>
                  <Input
                    value={form.slug}
                    onChange={(e) => setForm({ ...form, slug: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Logo URL</Label>
                  <Input
                    value={form.logo}
                    onChange={(e) => setForm({ ...form, logo: e.target.value })}
                    placeholder="https://example.com/logo.png"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Primary Color</Label>
                  <div className="flex gap-2">
                    <Input
                      value={form.primary_color}
                      onChange={(e) =>
                        setForm({ ...form, primary_color: e.target.value })
                      }
                      placeholder="#2563eb"
                    />
                    {form.primary_color && (
                      <div
                        className="w-9 h-9 rounded border shrink-0"
                        style={{ backgroundColor: form.primary_color }}
                      />
                    )}
                  </div>
                </div>
              </div>
              <div className="space-y-1">
                <Label>Timezone</Label>
                <Select
                  value={form.timezone}
                  onValueChange={(v) => setForm({ ...form, timezone: v })}
                >
                  <SelectTrigger className="w-full md:w-64">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIMEZONE_OPTIONS.map((tz) => (
                      <SelectItem key={tz} value={tz}>
                        {tz}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {error && (
                <p className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
                  {error}
                </p>
              )}
              <div className="flex gap-2 pt-2">
                <Button onClick={handleSave} disabled={saving}>
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-1" />
                  )}
                  Save Changes
                </Button>
                <Button variant="outline" onClick={toggleEdit}>
                  <X className="w-4 h-4 mr-1" />
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-500">
                  Organization Name
                </label>
                <p className="text-base mt-0.5">{organization.name}</p>
              </div>
              <Separator />
              <div>
                <label className="text-sm font-medium text-gray-500">
                  Slug
                </label>
                <p className="text-base mt-0.5 font-mono text-sm">
                  {organization.slug}
                </p>
              </div>
              <Separator />
              <div>
                <label className="text-sm font-medium text-gray-500">
                  Logo
                </label>
                <p className="text-base mt-0.5">
                  {organization.logo ? (
                    <span className="text-blue-600 break-all text-sm">
                      {organization.logo}
                    </span>
                  ) : (
                    <span className="text-gray-400 italic">None</span>
                  )}
                </p>
              </div>
              <Separator />
              <div>
                <label className="text-sm font-medium text-gray-500">
                  Primary Color
                </label>
                <p className="text-base mt-0.5 flex items-center gap-2">
                  {organization.primary_color ? (
                    <>
                      <span
                        className="w-4 h-4 rounded border inline-block"
                        style={{ backgroundColor: organization.primary_color }}
                      />
                      <span className="font-mono text-sm">
                        {organization.primary_color}
                      </span>
                    </>
                  ) : (
                    <span className="text-gray-400 italic">None</span>
                  )}
                </p>
              </div>
              <Separator />
              <div>
                <label className="text-sm font-medium text-gray-500">
                  Timezone
                </label>
                <p className="text-base mt-0.5">{organization.timezone}</p>
              </div>
              <Separator />
              <div>
                <label className="text-sm font-medium text-gray-500">
                  Created At
                </label>
                <p className="text-base mt-0.5 flex items-center gap-1">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  {formatDate(organization.created_at)}
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
